import { Selection } from "d3";
import { Moment } from "moment";
import {
    CalendarDrawerContent,
    CalendarDrawerSettings,
    CalendarState,
    Dimensions,
    Placement,
    SpiralItem,
} from "burde";
import { Page, SvgId, CalendarDimensions } from "svg/constants";
import { drawSvgDropShadow } from "svg/draw/draw-svg-dropshadow";
import drawLayout from "./dynamic-layout/draw-layout";
import {
    SvgCalendarLayout,
    LayoutFont,
    DayModuleElement,
    WeekModuleElement,
} from "ui/constants";
import { getNumberOfSpiralsFromCalendarSize } from "ui/helper";

const drawDualPage = (
    container: Selection<any, any, any, any>,
    calendar: CalendarState,
    calendarDrawerSettings: CalendarDrawerSettings,
    pageNumber: number,
    layout: SvgCalendarLayout,
    date: Moment,
    drawWeekModulePlaceHolder: boolean,
    Header_WeekNumberPrefix: string,
    fontData: LayoutFont | null,
    dayModuleElements: DayModuleElement[] | null,
    weekModuleElements: WeekModuleElement[] | null,
    calendarDrawerContent: CalendarDrawerContent,
) => {
    const calendarSize =
        calendar.calendarSize !== "" ? calendar.calendarSize : "A5";
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendarSize,
    );

    if (calendarDimensions == undefined) return;

    container.html("");

    const svg = container
        .append("svg")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("preserveAspectRatio", "xMidYMid meet")
        .attr(
            "viewBox",
            `0 0 ${
                calendarDimensions.contentWidthWithoutBleeds * 2 +
                calendarDimensions.pageGap +
                calendarDimensions.presentationEmptySpaceWidth * 2
            } ${
                calendarDimensions.contentHeightWithoutBleeds +
                calendarDimensions.presentationEmptySpaceHeight * 2
            }`,
        )
        .attr("id", `${SvgId}${pageNumber}`)
        .classed("svg-content", true);

    const leftPageDropShadowDimensions: Dimensions = {
        Width: calendarDimensions.contentWidthWithoutBleeds,
        Height: calendarDimensions.contentHeightWithoutBleeds,
    };

    const leftPageDropShadowPlacement: Placement = {
        X: calendarDimensions.presentationEmptySpaceWidth,
        Y: calendarDimensions.presentationEmptySpaceHeight,
    };

    drawSvgDropShadow(
        svg,
        leftPageDropShadowDimensions,
        leftPageDropShadowPlacement,
    );

    const leftSvgGroup = svg
        .append("g")
        .attr(
            "transform",
            `translate(${calendarDimensions.presentationEmptySpaceWidth},${calendarDimensions.presentationEmptySpaceHeight})`,
        )
        .attr("id", "leftPageGroup");

    const leftSvg = leftSvgGroup
        .append("svg")
        .attr("width", calendarDimensions.contentWidthWithoutBleeds)
        .attr("height", calendarDimensions.contentHeightWithoutBleeds)
        .attr(
            "viewBox",
            `0 0 ${calendarDimensions.contentWidthWithoutBleeds} ${calendarDimensions.contentHeightWithoutBleeds}`,
        )
        .classed("leftPageSvg", true);

    drawLayout(
        leftSvg,
        calendar,
        calendarDrawerSettings,
        Page.Odd,
        date,
        Header_WeekNumberPrefix,
        layout,
        fontData,
        drawWeekModulePlaceHolder,
        dayModuleElements,
        weekModuleElements,
        calendarDrawerContent,
    );

    let rightPageDropShadowDimensions: Dimensions = {
        Width: calendarDimensions.contentWidthWithoutBleeds,
        Height: calendarDimensions.contentHeightWithoutBleeds,
    };
    let rightPageDropShadowPlacement: Placement = {
        X:
            calendarDimensions.contentWidthWithoutBleeds +
            calendarDimensions.presentationEmptySpaceWidth +
            calendarDimensions.pageGap,
        Y: calendarDimensions.presentationEmptySpaceHeight,
    };

    drawSvgDropShadow(
        svg,
        rightPageDropShadowDimensions,
        rightPageDropShadowPlacement,
    );

    const rightSvgGroup = svg
        .append("g")
        .attr("id", "rightPageGroup")
        .attr(
            "transform",
            `translate(${
                calendarDimensions.contentWidthWithoutBleeds +
                calendarDimensions.pageGap +
                calendarDimensions.presentationEmptySpaceWidth
            }, ${calendarDimensions.presentationEmptySpaceHeight})`,
        );

    const rightSvg = rightSvgGroup
        .append("svg")
        .attr("width", calendarDimensions.contentWidthWithoutBleeds)
        .attr("height", calendarDimensions.contentHeightWithoutBleeds)
        .attr(
            "viewBox",
            `0 0 ${calendarDimensions.contentWidthWithoutBleeds} ${calendarDimensions.contentHeightWithoutBleeds}`,
        )
        .classed("rightPageSvg", true);

    drawLayout(
        rightSvg,
        calendar,
        calendarDrawerSettings,
        Page.Even,
        date,
        Header_WeekNumberPrefix,
        layout,
        fontData,
        drawWeekModulePlaceHolder,
        dayModuleElements,
        weekModuleElements,
        calendarDrawerContent,
    );

    const spiralImage = (calendarDrawerContent.spiral as SpiralItem).image.replace(
        "#",
        "double",
    );

    const numberOfSpirals = getNumberOfSpiralsFromCalendarSize(
        calendar.calendarSize,
    );

    const spiralHeightGap: number =
        (calendarDimensions.contentHeightWithoutBleeds -
            numberOfSpirals * calendarDimensions.spiralHeight) /
        (numberOfSpirals + 1);

    var spiralStartPos: number =
        calendarDimensions.presentationEmptySpaceHeight;
    for (let i = 0; i < numberOfSpirals; i++) {
        svg.append("svg:image")
            .attr(
                "x",
                calendarDimensions.contentWidthWithoutBleeds +
                    calendarDimensions.presentationEmptySpaceWidth +
                    calendarDimensions.pageGap / 2 -
                    calendarDimensions.spiralWidth / 2,
            )
            .attr("y", spiralStartPos + spiralHeightGap)
            .attr("width", calendarDimensions.spiralWidth)
            .attr("height", calendarDimensions.spiralHeight)
            .attr("xlink:href", `/images/${spiralImage}`);

        spiralStartPos += calendarDimensions.spiralHeight + spiralHeightGap;
    }
};

export { drawDualPage };
